import React, { useState, useEffect, useRef } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../authContext';
import './WalletBalance.css';

const WalletBalance = ({ showDetails = true, className = '', onBalanceChange }) => {
  const { currentUser } = useAuth();
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const onBalanceChangeRef = useRef(onBalanceChange);

  useEffect(() => {
    onBalanceChangeRef.current = onBalanceChange;
  }, [onBalanceChange]);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const userDocRef = doc(db, 'users', currentUser.uid);
    const unsubscribe = onSnapshot(
      userDocRef,
      (docSnapshot) => {
        try {
          const bal = docSnapshot.exists() ? (docSnapshot.data().rewardsBalance || 0) : 0;
          setBalance(bal);
          setError(null);
          onBalanceChangeRef.current && onBalanceChangeRef.current(bal);
        } catch (err) {
          console.error('Error processing wallet balance:', err);
          setError('Failed to load balance');
          setBalance(0);
          onBalanceChangeRef.current && onBalanceChangeRef.current(0);
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        console.error('Error listening to wallet balance:', err);
        setError('Failed to load balance');
        setBalance(0);
        setLoading(false);
        onBalanceChangeRef.current && onBalanceChangeRef.current(0);
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className={`wallet-balance ${className}`}>
        <div className="wallet-balance-loading">
          <div className="balance-skeleton"></div>
          {showDetails && <div className="balance-label-skeleton"></div>}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`wallet-balance error ${className}`}>
        <div className="balance-error">
          <span className="error-icon">⚠️</span>
          <span className="error-text">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`wallet-balance ${className}`}>
      <div className="balance-container">
        <div className="balance-amount">{formatCurrency(balance)}</div>
        {showDetails && (
          <div className="balance-details">
            <div className="balance-label">Available Balance</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WalletBalance;